import { Link } from "react-router-dom";
import { Icon, MOODS, type IconName } from "@wiseowl/design-system";
import "./design-system.css";

/*
 * /design-system — public design system documentation page.
 *
 * Direct port of the Claude Design handoff bundle's
 * `design-system.html` (~/Downloads/wiseowl 2/project/), adapted
 * to React. Every visual element resolves to @wiseowl/tokens so the
 * page IS the design system — not a screenshot of one.
 *
 * All page-specific styles live in design-system.css and are scoped
 * under `.ds-page` to avoid colliding with the rest of the app.
 *
 * Why we don't use the production <Button>, <Pill>, etc. components
 * for the live demos: the brand bundle's reference exposes the
 * showcase as plain HTML + CSS so that designers and engineers on
 * other frameworks can see the raw shape. Re-rendering through our
 * React components would obscure that. The "For developers" section
 * names the real components by file path for engineers to find.
 */

const LOGO_SRC = "/wot-logo-512.png";

const ICON_GRID: IconName[] = [
  "arrow", "back", "close", "plus", "send", "mic", "home", "book",
  "chat", "chart", "user", "flame", "spark", "leaf", "bookmark",
  "history", "bell", "search", "settings",
];

export const DesignSystem = () => (
  <div className="ds-page">
    <div className="ds-shell">

      {/* ── Top bar ─────────────────────────────────────── */}
      <div className="topbar">
        <Link to="/" className="brand-lockup" style={{ textDecoration: "none" }}>
          <span className="mark"><img src={LOGO_SRC} alt="Wise Owl" /></span>
          <span className="wordmark">Wise<span className="owl">Owl</span></span>
        </Link>
        <div className="topbar-meta">
          <span>v1.0 · May 2026</span>
          <span className="dot" />
          <Link to="/">App</Link>
          <a href="https://github.com" target="_blank" rel="noreferrer">Repo</a>
          <Link to="/sign-up">Get started</Link>
        </div>
      </div>

      {/* ── Hero ────────────────────────────────────────── */}
      <header className="hero">
        <div>
          <div className="wo-eyebrow wo-eyebrow--mustard" style={{ marginBottom: 20 }}>
            The Wise Owl design system
          </div>
          <h1>A wise pause<br />in a <em>noisy</em> world.</h1>
          <p className="lede">
            The single source of truth for everything Wise Owl — voice, color, type, components, and brand.
            Calm by default, warm when it matters, never preachy.
          </p>
        </div>
        <div className="hero-stats">
          <div className="hero-stat"><div className="k">Tokens</div><div className="v">62 <small>across 7 scales</small></div></div>
          <div className="hero-stat"><div className="k">Components</div><div className="v">23 <small>shipped to production</small></div></div>
          <div className="hero-stat"><div className="k">Type families</div><div className="v">Lora · Manrope · DM Mono</div></div>
          <div className="hero-stat"><div className="k">Surfaces</div><div className="v">Web app · Auth · Marketing</div></div>
        </div>
      </header>

      {/* ── 01 BRAND ────────────────────────────────────── */}
      <section id="brand">
        <SecHead num="01 / Brand" title={<>The mark<br />&amp; the wordmark</>}
          desc={<>The owl is the heart. Round mask on teal for chat avatars and identity moments; mustard halo on dark for the Companion state. The wordmark splits two words: <em>Wise</em> in ink, <em>Owl</em> in mustard.</>} />

        <div className="brand-grid">
          <div className="brand-card">
            <div className="meta">
              <div className="label">Primary mark</div>
              <div className="label" style={{ color: "rgb(var(--wo-teal))" }}>on cream</div>
            </div>
            <div className="stage"><div className="owl-mark-lg"><img src={LOGO_SRC} alt="Wise Owl" /></div></div>
            <div className="footnote">Default use across the app — sidebar, chat, share cards.</div>
          </div>
          <div className="brand-card dark">
            <div className="meta">
              <div className="label">Glow mark</div>
              <div className="label" style={{ color: "rgb(var(--wo-mustard))" }}>companion mode</div>
            </div>
            <div className="stage"><div className="owl-mark-lg glow"><img src={LOGO_SRC} alt="Wise Owl" /></div></div>
            <div className="footnote">Reserved for the deep-teal Companion chat surface. One per screen, never decoratively.</div>
          </div>
          <div className="brand-card cream">
            <div className="meta">
              <div className="label">Wordmark</div>
              <div className="label" style={{ color: "rgb(var(--wo-mustard))" }}>two words, always</div>
            </div>
            <div className="stage"><div className="wordmark-lg">Wise<span className="owl">Owl</span></div></div>
            <div className="footnote">Lora 500. Never substitute a sans. Never one word. Never all-mustard.</div>
          </div>
        </div>
      </section>

      {/* ── 02 VOICE ────────────────────────────────────── */}
      <section id="voice">
        <SecHead num="02 / Voice" title={<>Wise friend,<br />not coach.</>}
          desc="Reflective copy uses Lora italic; UI copy is plain Manrope. Always acknowledge before suggesting. Never therapy language, never exclamation marks, never emoji." />

        <div className="block">
          <BlockHead title="Copy do / avoid" note="Hero, CTA, empty states, insight intros" />
          <div className="voice-grid">
            <div className="voice-row good"><div className="tag">Do · hero</div><div className="copy">What are you holding onto that is quietly draining your energy?</div></div>
            <div className="voice-row avoid"><div className="tag">Avoid</div><div className="copy">What are your stressors today?</div></div>

            <div className="voice-row good"><div className="tag">Do · CTA</div><div className="copy copy-cta">Begin today&apos;s reset</div></div>
            <div className="voice-row avoid"><div className="tag">Avoid</div><div className="copy">Start Now!</div></div>

            <div className="voice-row good"><div className="tag">Do · empty state</div><div className="copy">A quieter mind, day by day.</div></div>
            <div className="voice-row avoid"><div className="tag">Avoid</div><div className="copy">You have no entries yet 😢</div></div>

            <div className="voice-row good"><div className="tag">Do · insight</div><div className="copy">Wise Owl noticed — you keep returning to the word &ldquo;later.&rdquo;</div></div>
            <div className="voice-row avoid"><div className="tag">Avoid</div><div className="copy">Analysis complete. 3 patterns detected.</div></div>
          </div>
        </div>

        <div className="block">
          <BlockHead title="Principles" note="Four rules that hold" />
          <div className="principles">
            <div className="principle"><div className="n">01</div><div className="title">Acknowledge first</div><div className="body">Every AI reflection opens by recognizing what was shared, not solving it.</div></div>
            <div className="principle"><div className="n">02</div><div className="title">Invite, don&apos;t instruct</div><div className="body">&ldquo;You might try…&rdquo; not &ldquo;Do this.&rdquo; Second person, gently.</div></div>
            <div className="principle"><div className="n">03</div><div className="title">Tiny actions</div><div className="body">&ldquo;Ten quiet minutes&rdquo; beats &ldquo;meditate for 10 min.&rdquo; No prescriptions.</div></div>
            <div className="principle"><div className="n">04</div><div className="title">Sentence case</div><div className="body">Titles &amp; buttons sentence case. Mono UPPERCASE only for eyebrows.</div></div>
          </div>
        </div>
      </section>

      {/* ── 03 COLOR ────────────────────────────────────── */}
      <section id="color">
        <SecHead num="03 / Color" title={<>Teal, mustard,<br />and a calm canvas.</>}
          desc="Teal is the brand voice. Mustard is the moment of warmth — a sunbeam, not a highlighter. All neutrals are warm and softly toned; never pure white, never pure black. Mood swatches are small color tokens, never page backgrounds." />

        <div className="block">
          <BlockHead title="Brand" note="Primary &amp; accent" />
          <div className="palette">
            <Swatch dark bg="rgb(var(--wo-teal))" name="Teal" token="--wo-teal" hex="#095269" ratio="AA · cream" usage="Primary CTAs, active nav, hero panels." />
            <Swatch dark bg="rgb(var(--wo-teal-2))" name="Teal · pressed" token="--wo-teal-2" hex="#07415A" ratio="AA · cream" usage="Hover and pressed state for teal." />
            <Swatch dark bg="rgb(var(--wo-teal-deep))" name="Ink / Teal deep" token="--wo-teal-deep" hex="#0B2730" ratio="AAA · cream" usage="Companion mode background & primary text." />
            <Swatch bg="rgb(var(--wo-mustard))" name="Mustard" token="--wo-mustard" hex="#E1A400" ratio="AA · ink" usage="Accent — warmth, today's action, owl ring." />
          </div>
        </div>

        <div className="block">
          <BlockHead title="Neutrals" note="Warm — saturation ≤ 0.02" />
          <div className="palette">
            <Swatch bg="rgb(var(--wo-cream))" name="Cream" token="--wo-cream" hex="#FAF6EE" usage="App canvas. Warm, never sterile." withDivider />
            <Swatch bg="rgb(var(--wo-cream-2))" name="Cream · panel" token="--wo-cream-2" hex="#F4ECDD" usage="Subtle panels & subdued sections." withDivider />
            <Swatch bg="rgb(var(--wo-paper))" name="Paper" token="--wo-paper" hex="#FFFCF5" usage="Cards and sheets — one tier above canvas." withDivider />
            <Swatch dark bg="rgb(var(--wo-ink-soft))" name="Ink · soft" token="--wo-ink-soft" hex="#3A4148" usage="Secondary text & body copy." />
          </div>
        </div>

        <div className="block">
          <BlockHead title="Mood palette" note="Eight feelings — chips &amp; dots only" />
          <div className="palette moods">
            {MOODS.map((m) => (
              <div key={m.id} className="mood-swatch">
                <div className="mood-dot" style={{ background: m.hex }} />
                <div className="text">
                  <div className="name">{m.name}</div>
                  <div className="tone">{m.tone}</div>
                  <div className="hex">{m.hex}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="block">
          <BlockHead title="Semantic" note="Use sparingly — never in marketing" />
          <div className="palette">
            <Swatch dark bg="rgb(var(--wo-success))" name="Success" token="--wo-success" hex="#2F8F66" />
            <Swatch dark bg="rgb(var(--wo-warning))" name="Warning" token="--wo-warning" hex="#C8901E" />
            <Swatch dark bg="rgb(var(--wo-danger))" name="Danger" token="--wo-danger" hex="#B4452C" />
            <Swatch dark bg="rgb(var(--wo-info))" name="Info" token="--wo-info" hex="#095269" />
          </div>
        </div>
      </section>

      {/* ── 04 TYPE ─────────────────────────────────────── */}
      <section id="type">
        <SecHead num="04 / Typography" title={<>Serif &amp; sans,<br />always together.</>}
          desc="Lora carries the brand voice — reflective, italic, considered. Manrope handles the UI — clear at small sizes. DM Mono is reserved for eyebrows and meta. A screen always pairs serif and sans; mono only as accent." />

        <div className="typegrid">
          <div className="type-card serif">
            <div className="family">Display · Reflective</div>
            <div className="name">Lora</div>
            <div className="specimen">Aa</div>
            <div className="weights"><span>400</span><span>500</span><span>600</span><span>700</span><span>italic</span></div>
            <div className="use">Headlines, reflective copy, italic prompts and quotes. The voice of the brand.</div>
          </div>
          <div className="type-card sans">
            <div className="family">UI · Body</div>
            <div className="name">Manrope</div>
            <div className="specimen">Aa</div>
            <div className="weights"><span>300</span><span>400</span><span>500</span><span>600</span><span>700</span><span>800</span></div>
            <div className="use">Every button, label, nav item, body copy. Clean and legible at 12 – 18 px.</div>
          </div>
          <div className="type-card mono">
            <div className="family">Meta · Eyebrow</div>
            <div className="name">DM Mono</div>
            <div className="specimen">Aa</div>
            <div className="weights"><span>400</span><span>500</span><span>UPPER</span></div>
            <div className="use">Eyebrows, timestamps, system labels, keyboard shortcuts. Always uppercase, letter-spaced.</div>
          </div>
        </div>

        <div className="block">
          <BlockHead title="Type scale" note="Mobile-first · bump 10 – 15% on desktop" />
          <div className="scale">
            <ScaleRow token="--wo-fs-display" size="30 / 1.18" desc="Page headlines — Lora 500, tight tracking.">
              <span className="wo-display">A quieter mind, day by day.</span>
            </ScaleRow>
            <ScaleRow token="--wo-fs-h1" size="26 / 1.32" desc="Section headers — Lora 500.">
              <span className="wo-h1">Today&apos;s 3-minute reset</span>
            </ScaleRow>
            <ScaleRow token="--wo-fs-h2" size="22 / 1.32" desc="Card titles, drawer titles — Lora.">
              <span className="wo-h2">Saved reflections</span>
            </ScaleRow>
            <ScaleRow token="--wo-fs-h3" size="18 / 1.32" desc="Inline headings — Manrope semibold.">
              <span className="wo-h3">Continue where you left off</span>
            </ScaleRow>
            <ScaleRow token="--wo-fs-body" size="15 / 1.45" desc="Body copy — Manrope regular.">
              <span className="wo-body">Notice the weight you&apos;ve been carrying without naming. Let it have a name for a moment.</span>
            </ScaleRow>
            <ScaleRow token="Reflective body" size="15 / 1.55" desc="AI reflections & prompts — Lora italic.">
              <span className="wo-reflective" style={{ fontStyle: "italic" }}>&ldquo;You might try spending ten quiet minutes today without trying to solve anything.&rdquo;</span>
            </ScaleRow>
            <ScaleRow token="--wo-fs-small" size="13 / 1.45" desc="Meta & footer copy — Manrope.">
              <span className="wo-small">Auto-saved 2 minutes ago</span>
            </ScaleRow>
            <ScaleRow token="--wo-fs-eyebrow" size="11 / 1.45" desc="Eyebrows & labels — DM Mono, UPPER, +1.4 letter-space.">
              <span className="wo-eyebrow">Today&apos;s 3-minute reset</span>
            </ScaleRow>
          </div>
        </div>

        <div className="specimen-quote">
          <div className="label">Voice specimen</div>
          <div className="lead" style={{ marginTop: 14 }}>What&apos;s quietly on your mind today?</div>
          <p className="body">Wise Owl is a wise companion, not therapy. It&apos;s the friend who notices the weight before you do — and offers a small, kind action you can actually take. Sentence case. Short sentences. Long thoughts.</p>
          <div className="wo-eyebrow" style={{ color: "rgb(var(--wo-teal))" }}>Lora italic · Manrope · DM Mono</div>
        </div>
      </section>

      {/* ── 05 SPACING ──────────────────────────────────── */}
      <section id="spacing">
        <SecHead num="05 / Spacing" title={<>Four-pixel<br />rhythm.</>}
          desc="Every space in the system is a multiple of four. Generous whitespace is non-negotiable — reflective copy needs room. Content max-width is 1200 px on desktop." />

        <div className="spacing-strip">
          {[
            ["s-1", 4], ["s-2", 8], ["s-3", 12], ["s-4", 16], ["s-5", 20],
            ["s-6", 24], ["s-7", 32], ["s-8", 40], ["s-9", 56], ["s-10", 72],
          ].map(([lbl, px]) => (
            <div key={lbl} className="sp">
              <div className="bar" style={{ height: px as number }} />
              <div className="lbl">{lbl}</div>
              <div className="px">{px}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── 06 RADII ────────────────────────────────────── */}
      <section id="radii">
        <SecHead num="06 / Radii" title={<>Five corner<br />moods.</>}
          desc="Chips small, cards round, hero panels softest, buttons fully pilled. Sharp corners are not in the system." />

        <div className="radii">
          <div className="radius-card r-sm"><div className="shape" /><div><div className="name">Small</div><div className="px">10 px · chips</div></div></div>
          <div className="radius-card r-md"><div className="shape" /><div><div className="name">Medium</div><div className="px">14 px · inputs</div></div></div>
          <div className="radius-card r-lg"><div className="shape" /><div><div className="name">Large</div><div className="px">18 px · cards</div></div></div>
          <div className="radius-card r-xl"><div className="shape" /><div><div className="name">Extra-large</div><div className="px">22 px · hero panels</div></div></div>
          <div className="radius-card r-pill"><div className="shape" /><div><div className="name">Pill</div><div className="px">9999 px · buttons</div></div></div>
        </div>
      </section>

      {/* ── 07 SHADOWS ──────────────────────────────────── */}
      <section id="shadows">
        <SecHead num="07 / Elevation" title={<>Two tiers,<br />plus a halo.</>}
          desc="Resting shadow is barely visible. Lifted shadow is for hover and popovers. The mustard halo is exclusive to the Companion-mode owl avatar." />

        <div className="shadows">
          <div className="shadow-card s1">
            <div><div className="name">Resting</div><div className="token">--wo-shadow-1</div></div>
            <div className="desc">Card lift on cream. Almost invisible at rest — the world is calm.</div>
          </div>
          <div className="shadow-card s2">
            <div><div className="name">Lifted</div><div className="token">--wo-shadow-2</div></div>
            <div className="desc">Hover &amp; popovers. A gentle separation from the canvas.</div>
          </div>
          <div className="shadow-card glow">
            <div className="glow-mark"><img src={LOGO_SRC} alt="Wise Owl" /></div>
            <div><div className="name">Glow</div><div className="token">--wo-shadow-glow</div></div>
            <div className="desc">Companion-mode owl avatar only. A warm mustard halo on deep teal.</div>
          </div>
        </div>
      </section>

      {/* ── 08 ICONS ────────────────────────────────────── */}
      <section id="icons">
        <SecHead num="08 / Iconography" title={<>Line, 1.6.<br />No emoji, ever.</>}
          desc={<>Custom line icons drawn at 1.6 – 1.8 stroke on a 24 × 24 viewBox. Round caps, round joins, no fills. Stroke uses <code>currentColor</code>. Nineteen in the set today.</>} />

        <div className="icons">
          {ICON_GRID.map((name) => (
            <div key={name} className="icon-card">
              <Icon name={name} size={22} />
              <div className="name">{name}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── 09 COMPONENTS ───────────────────────────────── */}
      <section id="components">
        <SecHead num="09 / Components" title={<>Built from<br />the same tokens.</>}
          desc={<>Every component below is in production today. Source lives in the <code>@wiseowl/design-system</code> package — drop the tokens stylesheet in your <code>&lt;head&gt;</code> and they port to any framework.</>} />

        <div className="comp-grid">

          {/* Buttons */}
          <div className="comp-card">
            <div className="title-row"><div className="h">Buttons</div><div className="tag">4 variants · 3 sizes</div></div>
            <div className="stage">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                <button className="btn btn-primary">Begin today&apos;s reset</button>
                <button className="btn btn-mustard">Mark done · day 13</button>
                <button className="btn btn-ghost">Save reflection</button>
                <button className="btn btn-dark">Talk to Wise Owl</button>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
                <button className="btn btn-primary btn-lg">Begin today&apos;s reset</button>
                <button className="btn btn-primary btn-sm">Continue</button>
                <button className="btn btn-mustard btn-icon" aria-label="Begin reset">
                  <Icon name="spark" size={18} />
                </button>
              </div>
            </div>
          </div>

          {/* Inputs */}
          <div className="comp-card">
            <div className="title-row"><div className="h">Inputs &amp; search</div><div className="tag">Focus = teal ring</div></div>
            <div className="stage">
              <div>
                <label className="input-label" htmlFor="ds-email">Email</label>
                <input id="ds-email" className="input" type="email" placeholder="you@example.com" defaultValue="aisha@gmail.com" />
              </div>
              <div>
                <span className="input-label">OTP code</span>
                <div className="otp-row">
                  <div className="otp filled">4</div><div className="otp filled">2</div><div className="otp filled">7</div>
                  <div className="otp filled">1</div><div className="otp" /><div className="otp" />
                </div>
              </div>
              <div className="search-pill">
                <Icon name="search" size={16} />
                <span>Search reflections, prompts, themes…</span>
                <span className="kbd">⌘K</span>
              </div>
            </div>
          </div>

          {/* Pills */}
          <div className="comp-card">
            <div className="title-row"><div className="h">Pills &amp; chips</div><div className="tag">Tags, moods, streaks</div></div>
            <div className="stage">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <span className="pill pill-teal">Day 13 of 21</span>
                <span className="pill pill-mustard">+ Today&apos;s action</span>
                <span className="pill pill-success">Saved</span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {(["calm", "lonely", "confused", "overthinking"] as const).map((id) => {
                  const m = MOODS.find((x) => x.id === id)!;
                  return (
                    <span key={id} className="pill pill-mood">
                      <span className="moddot" style={{ background: m.hex }} />
                      {m.name}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Mood dots */}
          <div className="comp-card">
            <div className="title-row"><div className="h">Mood selector</div><div className="tag">Tap to choose</div></div>
            <div className="stage">
              <div className="mood-row">
                {MOODS.slice(0, 6).map((m) => (
                  <div key={m.id} className="mood-pick">
                    <div className={`d${m.id === "stressed" ? " active" : ""}`} style={{ background: m.hex }} />
                    <div className="n">{m.name}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Cards */}
          <div className="comp-card">
            <div className="title-row"><div className="h">Cards</div><div className="tag">Resting · Hero · Insight</div></div>
            <div className="stage">
              <div className="card-hero">
                <div className="e">Today&apos;s 3-minute reset</div>
                <div className="t">What&apos;s quietly on your mind?</div>
                <button className="btn btn-mustard btn-sm">Begin →</button>
              </div>
              <div className="card-insight">
                <div className="e">Wise Owl noticed</div>
                <div className="t">You return to the word &ldquo;later&rdquo; most evenings.</div>
              </div>
            </div>
          </div>

          {/* Chat */}
          <div className="comp-card dark">
            <div className="title-row"><div className="h">Companion chat</div><div className="tag">Deep teal · serif owl · sans user</div></div>
            <div className="stage">
              <div className="chat">
                <div className="bubble-row">
                  <div className="bubble-avatar"><img src={LOGO_SRC} alt="" /></div>
                  <div className="bubble-owl">What&apos;s been sitting with you today?</div>
                </div>
                <div className="bubble-user">I keep going back to the conversation with my sister.</div>
                <div className="bubble-status">
                  <div className="bubble-avatar" style={{ width: 20, height: 20, boxShadow: "none" }}>
                    <img src={LOGO_SRC} alt="" style={{ width: 22, height: 22 }} />
                  </div>
                  <span>WISE OWL · sensing emotional weight…</span>
                </div>
              </div>
            </div>
          </div>

          {/* Streak */}
          <div className="comp-card">
            <div className="title-row"><div className="h">Streak panel</div><div className="tag">21-day mind reset</div></div>
            <div className="stage">
              <div className="streak">
                <div className="eyebrow">9 days to go</div>
                <div className="num">13</div>
                <div className="copy">Nine days until your 21-Day Mind Reset.</div>
                <div className="streak-cells">
                  {Array.from({ length: 21 }).map((_, i) => (
                    <div key={i} className={`cell${i < 13 ? " on" : ""}`} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar nav */}
          <div className="comp-card">
            <div className="title-row"><div className="h">Sidebar nav · desktop</div><div className="tag">268 px sticky</div></div>
            <div className="stage">
              <div className="sidebar">
                <div className="side-row active"><Icon name="home" size={18} /> Home</div>
                <div className="side-row"><Icon name="book" size={18} /> Reflect</div>
                <div className="side-row"><Icon name="chat" size={18} /> Companion</div>
                <div className="side-row"><Icon name="bookmark" size={18} /> Library</div>
              </div>
            </div>
          </div>

          {/* Bottom nav */}
          <div className="comp-card">
            <div className="title-row"><div className="h">Bottom nav · mobile</div><div className="tag">76 px incl. safe area</div></div>
            <div className="stage">
              <div className="nav-mobile">
                <div className="nav-tab active"><Icon name="home" size={22} /><div className="lbl">Home</div></div>
                <div className="nav-tab"><Icon name="book" size={22} /><div className="lbl">Reflect</div></div>
                <div className="nav-tab"><Icon name="spark" size={22} /><div className="lbl" style={{ color: "rgb(var(--wo-mustard))" }}>Reset</div></div>
                <div className="nav-tab"><Icon name="chat" size={22} /><div className="lbl">Owl</div></div>
                <div className="nav-tab"><Icon name="bookmark" size={22} /><div className="lbl">Library</div></div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ── 10 MOTION ───────────────────────────────────── */}
      <section id="motion">
        <SecHead num="10 / Motion" title={<>Soft easing,<br />short durations.</>}
          desc="No bounces, no parallax, no aggressive entrances. The breathing pulse is the only loop. Everything else fades or scales gently and resolves." />

        <div className="motion-grid">
          <div className="motion-card">
            <div className="k">--wo-ease-soft</div>
            <div className="v">cubic-bezier(.4, 0, .2, 1)</div>
            <div className="d">A single easing curve everywhere. Soft start, soft finish.</div>
          </div>
          <div className="motion-card">
            <div className="k">Durations</div>
            <div className="v">120 · 180 · 320 ms</div>
            <div className="d">Fast (state) · Mid (entrance) · Slow (page transition).</div>
          </div>
          <div className="motion-card">
            <div className="k">Breathing pulse</div>
            <div className="v">4 s · ease-in-out · ∞</div>
            <div className="d">Reserved for the Reset flow&apos;s breathing circle. Never decorative.</div>
          </div>
        </div>
      </section>

      {/* ── 11 DEV USAGE ────────────────────────────────── */}
      <section id="developers">
        <SecHead num="11 / For developers" title={<>Drop in once.<br />Use everywhere.</>}
          desc={<>Tokens are CSS custom properties. Reach for <code>var(--wo-*)</code> in every component — never hard-code a hex. Components are framework-light React + Tailwind; tokens live in <code>@wiseowl/tokens</code> and components in <code>@wiseowl/design-system</code>.</>} />

        <div className="dev-grid">
          <pre className="code">
{`/* 1 · Add tokens once at the root */
import "@wiseowl/tokens/tokens.css";

/* 2 · Use semantic type classes */
<h1 className="wo-display">A quieter mind.</h1>
<p  className="wo-reflective">What's on your mind?</p>
<span className="wo-eyebrow">Today's reset</span>

/* 3 · Or compose with tokens */
.my-card {
  background:    var(--wo-paper);
  border:        1px solid var(--wo-line);
  border-radius: var(--wo-r-lg);
  padding:       var(--wo-s-6);
  box-shadow:    var(--wo-shadow-1);
  transition:    background var(--wo-dur-fast)
                 var(--wo-ease-soft);
}`}
          </pre>

          <div className="resources">
            <a href="/" target="_self">
              <div>
                <div className="name">The Wise Owl app</div>
                <div className="path">/ · live product</div>
              </div>
              <div className="arrow">→</div>
            </a>
            <a href="/sign-up" target="_self">
              <div>
                <div className="name">Sign up</div>
                <div className="path">/sign-up · demo-mode auth</div>
              </div>
              <div className="arrow">→</div>
            </a>
            <a href="/home" target="_self">
              <div>
                <div className="name">Home view</div>
                <div className="path">/home · daily reset, mood row, tools</div>
              </div>
              <div className="arrow">→</div>
            </a>
            <a href="/library" target="_self">
              <div>
                <div className="name">Library view</div>
                <div className="path">/library · patterns, history, saved</div>
              </div>
              <div className="arrow">→</div>
            </a>
            <a href="https://fonts.google.com/specimen/Lora" target="_blank" rel="noreferrer">
              <div>
                <div className="name">Lora · Manrope · DM Mono</div>
                <div className="path">Google Fonts</div>
              </div>
              <div className="arrow">→</div>
            </a>
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────── */}
      <div className="footer">
        <div>© Wise Owl · A wise pause in a noisy world</div>
        <div>v1.0 · Last reviewed May 21, 2026</div>
      </div>

    </div>
  </div>
);

/* ── Small layout helpers (private to this page) ──── */

const SecHead = ({
  num, title, desc,
}: {
  num: string;
  title: React.ReactNode;
  desc: React.ReactNode;
}) => (
  <div className="sec-head">
    <div className="sec-num">{num}</div>
    <div className="sec-title">{title}</div>
    <div className="sec-desc">{desc}</div>
  </div>
);

const BlockHead = ({ title, note }: { title: string; note: React.ReactNode }) => (
  <div className="block-head">
    <div className="block-title">{title}</div>
    <div className="block-note">{note}</div>
  </div>
);

const Swatch = ({
  dark, bg, name, token, hex, ratio, usage, withDivider,
}: {
  dark?: boolean;
  bg: string;
  name: string;
  token?: string;
  hex: string;
  ratio?: string;
  usage?: string;
  withDivider?: boolean;
}) => (
  <div className={`swatch${dark ? " dark" : ""}`}>
    <div
      className="chip"
      style={{ background: bg, borderBottom: withDivider ? "1px solid rgb(var(--wo-line))" : undefined }}
    >
      {ratio && <span className="ratio">{ratio}</span>}
    </div>
    <div className="meta">
      <div className="name">{name}</div>
      {token && <div className="token">{token}</div>}
      <div className="hex">{hex}</div>
      {usage && <div className="usage">{usage}</div>}
    </div>
  </div>
);

const ScaleRow = ({
  token, size, desc, children,
}: {
  token: string;
  size: string;
  desc: string;
  children: React.ReactNode;
}) => (
  <div className="scale-row">
    <div className="token-name">{token}</div>
    <div className="size">{size}</div>
    <div className="sample">{children}</div>
    <div className="desc">{desc}</div>
  </div>
);
